// Fixed wikiImageFetcher.js - correcting variable name conflicts

export const fetchWikipediaImage = async (speciesName) => {
    try {
      // Step 1: Search directly for the specific species page first (most reliable)
      const exactSearchUrl = `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(
        speciesName
      )}&prop=pageimages&format=json&origin=*&pithumbsize=500&pilimit=1`;
      
      const exactResponse = await fetch(exactSearchUrl);
      const exactData = await exactResponse.json();
      
      const exactPages = exactData.query.pages;
      const exactPageId = Object.keys(exactPages)[0];
      
      // If we found an exact match page with a thumbnail, use it
      if (exactPageId !== "-1" && exactPages[exactPageId].thumbnail) {
        // We have a direct thumbnail match - highest confidence
        const pageUrl = `https://en.wikipedia.org/wiki/${encodeURIComponent(speciesName)}`;
        const imageInfo = {
          url: exactPages[exactPageId].thumbnail.source,
          title: `${speciesName} image`,
          wikiPageUrl: pageUrl, // Add Wikipedia page URL
          attribution: {
            artist: "Wikimedia Commons",
            license: "See Wikipedia",
            licenseUrl: pageUrl,
            source: 'Wikimedia Commons'
          }
        };
        
        console.log(`Direct thumbnail match found for ${speciesName}`);
        return imageInfo;
      }
      
      // Step 2: If no direct match, search for the page
      const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(
        speciesName
      )}&format=json&origin=*`;
      
      const searchResponse = await fetch(searchUrl);
      const searchData = await searchResponse.json();
      
      if (!searchData.query.search.length) {
        console.log(`No Wikipedia page found for: ${speciesName}`);
        return null;
      }
      
      // Get the page title from search results
      const pageTitle = searchData.query.search[0].title;
      const wikiPageUrl = `https://en.wikipedia.org/wiki/${encodeURIComponent(pageTitle)}`;
      
      // Get the page with its thumbnail (key improvement)
      const pageUrl = `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(
        pageTitle
      )}&prop=pageimages&format=json&origin=*&pithumbsize=500`;
      
      const pageResponse = await fetch(pageUrl);
      const pageData = await pageResponse.json();
      
      const thumbPages = pageData.query.pages;
      const thumbPageId = Object.keys(thumbPages)[0];
      
      // If the page has a thumbnail, use it (this is the main image Wikipedia shows)
      if (thumbPages[thumbPageId].thumbnail) {
        console.log(`Thumbnail found for ${pageTitle}`);
        
        return {
          url: thumbPages[thumbPageId].thumbnail.source,
          title: `${pageTitle} image`,
          wikiPageUrl: wikiPageUrl, // Add Wikipedia page URL
          attribution: {
            artist: "Wikimedia Commons",
            license: "See Wikipedia",
            licenseUrl: wikiPageUrl,
            source: 'Wikimedia Commons'
          }
        };
      }
      
      // Fallback to original approach if no thumbnail found
      // Then get the page images
      const imagesUrl = `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(
        pageTitle
      )}&prop=images&format=json&origin=*`;
      
      const imagesResponse = await fetch(imagesUrl);
      const imagesData = await imagesResponse.json();
      
      // Extract page ID and images
      const imagePages = imagesData.query.pages;
      const imagePageId = Object.keys(imagePages)[0];
      
      if (!imagePages[imagePageId].images || !imagePages[imagePageId].images.length) {
        console.log(`No images found for: ${pageTitle}`);
        return null;
      }
      
      // Filter out SVG files and logos, look for JPG/PNG files
      const relevantImages = imagePages[imagePageId].images.filter(img => {
        const title = img.title.toLowerCase();
        return (
          (title.includes('.jpg') || title.includes('.png') || title.includes('.gif')) &&
          !title.includes('logo') &&
          !title.includes('icon') &&
          !title.includes('map')
        );
      });
      
      if (!relevantImages.length) {
        console.log(`No suitable images found for: ${pageTitle}`);
        return null;
      }
      
      // Get image info for the first suitable image
      const imageTitle = relevantImages[0].title;
      const imageInfoUrl = `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(
        imageTitle
      )}&prop=imageinfo&iiprop=url|extmetadata&format=json&origin=*`;
      
      const imageInfoResponse = await fetch(imageInfoUrl);
      const imageInfoData = await imageInfoResponse.json();
      
      const imageInfoPages = imageInfoData.query.pages;
      const imageInfoPageId = Object.keys(imageInfoPages)[0];
      
      if (!imageInfoPages[imageInfoPageId].imageinfo || !imageInfoPages[imageInfoPageId].imageinfo.length) {
        console.log(`No image info found for: ${imageTitle}`);
        return null;
      }
      
      const imageInfo = imageInfoPages[imageInfoPageId].imageinfo[0];
      
      // Get license information
      const metadata = imageInfo.extmetadata || {};
      const artist = metadata.Artist ? metadata.Artist.value : 'Wikimedia Commons';
      const license = metadata.License ? metadata.License.value : 'See Wikipedia';
      const licenseUrl = metadata.LicenseUrl ? metadata.LicenseUrl.value : '';
      
      return {
        url: imageInfo.url,
        title: imageTitle.replace('File:', ''),
        wikiPageUrl: wikiPageUrl, // Add Wikipedia page URL
        attribution: {
          artist: artist,
          license: license,
          licenseUrl: licenseUrl,
          source: 'Wikimedia Commons'
        }
      };
    } catch (error) {
      console.error(`Error fetching Wikipedia image for ${speciesName}:`, error);
      return null;
    }
  };